import React from 'react';
import PropTypes from 'prop-types';
import SelectButton from './SelectButton';
import Tooltip from '../Tooltip'
import classNames from 'classnames'
import styles from './Select.module.scss';
import SelectMenu from './SelectMenu';

class Select extends React.PureComponent {
  constructor (props, context) {
    super(props, context);

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.selectClick = this.selectClick.bind(this);

    this.state = {
      menuOpen: false,
      options: this.props.options || [],
      selectedOptions: this.props.defaultSelections || []
    }
  }

  componentWillMount () {
    document.addEventListener('mousedown', this.handleClick, false)
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClick)
  }

  componentDidUpdate (prevProps) {
    if (this.props.reset !== prevProps.reset) {
      if (this.props.reset) this.setState({ selectedOptions: this.props.defaultSelections || [] })
    }

    if (this.props.options !== prevProps.options) {
      this.setState({ options: this.props.options })
    }
  }

  selectClick () {
    if (this.props.disable || this.props.options.length < 1) return;

    this.setState({
      menuOpen: !this.state.menuOpen,
    })
  }

  handleClick (e) {
    if (!this.node.contains(e.target)) {
      this.setState({ menuOpen: false})
    }
  }

  handleChange (option) {
    if (option.inactive) return;

    if (option.clear) {
      this.setState({
        menuOpen: false,
        selectedOptions: []
      }, () => this.props.onSelect(this.state.selectedOptions, this.props.name));
    }else if (this.props.multiselect) {
      const selectedOptions = [...this.state.selectedOptions];
      let valueIndex = false;
      for (var i in selectedOptions) {
        if (selectedOptions[i].id === option.id) {
          valueIndex = i;
          break;
        }
      }

      (valueIndex) && selectedOptions.splice(valueIndex, 1);
      
      this.setState({
        selectedOptions:  (valueIndex) ? selectedOptions : [...selectedOptions, option]
      }, () => {
        if (this.props.onSelect) {
          this.props.onSelect(this.state.selectedOptions, this.props.name)
        }
      })
    } else {
      this.setState({ selectedOptions: [option]}, () => {
        this.setState({
          menuOpen: false
        });
        if (this.props.onSelect) {
          this.props.onSelect(this.state.selectedOptions, this.props.name);
        }
      })
    }
  }

  handleSearch (val) {
    let searchOptions = this.props.options.filter(option => {
      return option.value.toLowerCase().includes(val.toLowerCase()) && option.value;
    });

    if (searchOptions.length <= 0) {
      searchOptions = [{id: 0, value: 'No options', inactive: true}]
    }
    this.setState({
      options: searchOptions
    });
  }

  render () {
    const { buttonAlign, className, clearOption, description, design, disable, error, hideRequired, isSearchable, multiselect, options, overrideValue, prefix, required, size, title, tooltip } = this.props;
    const selectOptions = (clearOption && this.state.selectedOptions.length > 0) ? [{id: 0, value: 'Clear', clear: true}, ...this.state.options] : this.state.options;
    let selectValues = [];
    const isRequired = required && !hideRequired ? '*' : ''
    for (var v in this.state.selectedOptions) {
      selectValues = [...selectValues, this.state.selectedOptions[v].value]
    }

    const value = overrideValue || selectValues.join(', ');
    const formButton = <SelectButton
      title={value || `${title}${isRequired}`}
      buttonAlign={buttonAlign}
      design={design}
      size={size}
      isSearchable={isSearchable && !multiselect}
      onSearch={this.handleSearch}
      handleClick={this.selectClick}
      disable={disable || options.length < 1}
      caret
      value={value}
      isOpen={this.state.menuOpen} />
    
    return (
      <div id={`select${title && title.replace(/\s/g,'')}`} value={selectValues.toString()} className={classNames(className, styles.container)} ref={node => this.node = node}>
        {(design === 'default' && selectValues && selectValues.length > 0) && <label className={classNames(styles.label, {
          [styles.active]: this.state.menuOpen,
          [styles.small]: size === 'small'
        })}>{title} {isRequired}</label>}
        {tooltip && <Tooltip className={styles.tooltip} text={selectValues.join(', ')} size={size} delay={350} disable={this.state.menuOpen}>
          {formButton}
        </Tooltip>}
        {!tooltip && formButton}
        {
          (error || this.state.error || description) &&
          <span className={styles.inputNote}>
            {description && <span id='desc' className={styles.descMessage}>
              {description}
            </span>}
            {(error || this.state.error) && <span id='inputError' className={styles.errorMessage}>
              {error || this.state.error}
            </span>}
          </span>
        }

        {this.state.menuOpen && 
          <SelectMenu 
            options={selectOptions} 
            selected={this.state.selectedOptions} 
            handleChange={this.handleChange}
            prefix={prefix} 
            multiselect={multiselect}
            size={size} />
        }
      </div>
    )
  }
}

Select.propTypes = {
  className: PropTypes.string,
  /** Title of select component will appear as placeholder AND label above on focus/selection has been made*/
  title: PropTypes.string,
  /** Name to indentify select component. Useful when uses more than 1 component */
  name: PropTypes.string,
  /** Array of objects, each having id and value properties */
  options: PropTypes.arrayOf(PropTypes.shape({
    /** Option's ID */
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Option's value to be rendered in select item */
    value: PropTypes.string.isRequired
  })).isRequired,
  /** Required field? */
  required: PropTypes.bool,
  /** text description under select component */
  description: PropTypes.string,
  /** override selected object's value property */
  overrideValue: PropTypes.string,
  /** Array of objects to have selected by default */
  defaultSelections: PropTypes.array,
  /** Provide option to clear selection(s) */
  clearOption: PropTypes.bool,
  /** Search select options (Ony available for single select) */
  isSearchable: PropTypes.bool,
  /** Design Style */
  design: PropTypes.oneOf(['filter', 'standard', 'default']),
  /** Ability to select mutli options */
  multiselect: PropTypes.bool,
  /** Display an error message under select button */
  error: PropTypes.string,
  /** Function to be called upon selection. Will return array of selectd object(s) */
  onSelect: PropTypes.func,
  /** Used to displayed current selected options when hovering over main select button */
  tooltip: PropTypes.bool,
  /** Disable select */
  disable: PropTypes.bool,
  /** Alignment of text within select button */
  buttonAlign: PropTypes.oneOf(['left', 'right']),
  /** When true the select component will reset to its default selectedOptions state */
  reset: PropTypes.bool,
  /* Size of table: font, row height */
  size: PropTypes.oneOf(['small', 'default'])
}

Select.defaultProps = {
  multiselect: false,
  design: 'default',
  tooltip: false,
  size: 'default',
  buttonAlign: 'left',
  isSearchable: false,
  clearOption: false
}

export default Select;